/**
 * Menu item for changing the color of SKU labels via right-click context menu
 * Uses nested submenu pattern - "Change SKU Color" shows flyout with color options
 */
import { type ContextMenuList } from '../types/context-menu-list.type';
import { ContextMenuListItemType } from '../types/context-menu-list-item-type.enum';
import { DisplayMode } from '@r-core';
import { SkuColorPreset } from '../../viewspace/helpers/sku-labels';

/**
 * Available SKU label colors
 */
export const SKU_COLOR_OPTIONS = {
	green: { label: 'Green (Utility)', color: SkuColorPreset.Green },
	pink: { label: 'Pink (Vent)', color: SkuColorPreset.Pink },
	purple: { label: 'Purple (Appliance)', color: SkuColorPreset.Purple },
	blue: { label: 'Blue (Cutout/Custom)', color: SkuColorPreset.Blue },
	orange: { label: 'Orange', color: SkuColorPreset.Orange },
	red: { label: 'Red', color: SkuColorPreset.Red },
	yellow: { label: 'Yellow', color: SkuColorPreset.LightYellow },
	white: { label: 'White', color: SkuColorPreset.White },
	gray: { label: 'Gray', color: SkuColorPreset.Gray }
} as const;

/**
 * Creates a menu item for a specific color option (used in the nested submenu)
 */
const createColorMenuItem = (colorKey: keyof typeof SKU_COLOR_OPTIONS): ContextMenuList => {
	const colorOption = SKU_COLOR_OPTIONS[colorKey];
	return {
		type: ContextMenuListItemType.Item,
		content: colorOption.label,
		displayModes: [DisplayMode.Overhead, DisplayMode.OverheadManufacturing, DisplayMode.SkuOverhead],
		onClick: ({ refMap, controller }): void => {
			// Get the panel item from the reference map
			if (refMap?.panelItem) {
				const panelItem = refMap?.panelItem;

				// Store the custom color on the item using the new skuColor property
				panelItem.item.skuColor = colorOption.color;

				// Trigger a re-render by publishing an update event
				controller.publish('sku-label-color', 'updated', { panelItemId: panelItem.id, color: colorOption.color });
			}
		}
	};
};

/**
 * Menu item for changing SKU label color
 * Uses SubMenu type to create a nested flyout menu:
 * - First level: "Change SKU Color" menu item with arrow
 * - Second level: Flyout with color options
 */
export const skuColorMenuItem = (): ContextMenuList => {
	return {
		type: ContextMenuListItemType.SubMenu,
		label: 'Change SKU Color',
		displayModes: [DisplayMode.SkuOverhead, DisplayMode.SkuOverheadManufacturing],
		isVisible: ({ controller }): boolean => {
			return !controller.readonly;
		},
		items: [
			createColorMenuItem('green'),
			createColorMenuItem('pink'),
			createColorMenuItem('purple'),
			createColorMenuItem('blue'),
			createColorMenuItem('orange'),
			createColorMenuItem('red'),
			createColorMenuItem('yellow'),
			createColorMenuItem('white'),
			createColorMenuItem('gray')
		]
	};
};

/**
 * Menu item for resetting SKU label color to default
 */
export const resetSkuColorMenuItem = (): ContextMenuList => {
	return {
		type: ContextMenuListItemType.Item,
		content: 'Reset SKU Color',
		displayModes: [DisplayMode.SkuOverhead, DisplayMode.SkuOverheadManufacturing],
		isVisible: ({ controller, refMap }): boolean => {
			if (controller.readonly) return false;
			return refMap?.panelItem?.item?.skuColor !== undefined && refMap?.panelItem?.item?.skuColor !== null;
		},
		onClick: ({ refMap, controller }): void => {
			if (refMap?.panelItem) {
				const panelItem = refMap.panelItem;

				// Remove the custom color using the new skuColor property
				panelItem.item.skuColor = null;

				// Trigger a re-render
				controller.publish('sku-label-color', 'updated', { panelItemId: panelItem.id, color: null });
			}
		}
	};
};
